import jsyaml from "js-yaml";

const YAML_OPTIONS = {
  lineWidth: -1,
  noRefs: true,
};

const hasRenderableValue = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const renderField = (key, value) =>
  jsyaml.dump({ [key]: value }, YAML_OPTIONS).trimEnd();

const renderTemplate = (template, model) =>
  template
    .map((section) => {
      const lines = section.fields
        .filter((field) => {
          if (field.when && !field.when(model)) return false;
          return field.includeEmpty || hasRenderableValue(field.value(model));
        })
        .map((field) => renderField(field.key, field.value(model)));

      if (!lines.length) return null;

      return [`# ${section.title}`, ...lines].join("\n");
    })
    .filter(Boolean)
    .join("\n\n");

const normalizeDatasets = (selectedDatasets) =>
  (selectedDatasets || []).map((ds) => ({
    dataset_name: ds.title,
  }));

const parseOptionalInt = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const workspacePreviewTemplate = [
  {
    title: "Basics",
    fields: [
      { key: "experiment_type", value: (model) => model.experimentType },
      { key: "image", value: (model) => model.image },
      { key: "workspace_label", value: (model) => model.workspaceLabel },
    ],
  },
  {
    title: "Compute Resources",
    fields: [
      { key: "group_name", value: (model) => model.groupName },
      { key: "node_count", value: (model) => model.nodeCount },
      { key: "gpu_product", value: (model) => model.gpuProduct },
      { key: "gpu_count", value: (model) => model.gpuCount },
      { key: "cpu_request_cores", value: (model) => model.cpuRequestCores },
      { key: "memory_request_gb", value: (model) => model.memoryRequestGb },
    ],
  },
  {
    title: "Storage",
    fields: [
      {
        key: "burst_storage_enabled",
        value: (model) => model.burstStorageEnabled,
        when: (model) => model.workspaceType === "gpu",
      },
      {
        key: "datasets",
        value: (model) => model.datasets,
        includeEmpty: true,
      },
    ],
  },
];

const jobPreviewTemplate = [
  {
    title: "Basics",
    fields: [
      { key: "experiment_type", value: (model) => model.experimentType },
      { key: "image", value: (model) => model.image },
      { key: "job_label", value: (model) => model.jobLabel },
      { key: "walltime_hours", value: (model) => model.walltimeHours },
    ],
  },
  {
    title: "Compute Resources",
    fields: [
      { key: "group_name", value: (model) => model.groupName },
      { key: "node_count", value: (model) => model.nodeCount },
      { key: "gpu_product", value: (model) => model.gpuProduct },
      { key: "gpu_count", value: (model) => model.gpuCount },
      { key: "cpu_request_cores", value: (model) => model.cpuRequestCores },
      { key: "memory_request_gb", value: (model) => model.memoryRequestGb },
    ],
  },
  {
    title: "Storage",
    fields: [
      {
        key: "datasets",
        value: (model) => model.datasets,
        includeEmpty: true,
      },
    ],
  },
  {
    title: "Execution",
    fields: [
      {
        key: "commands",
        value: (model) => model.commands,
        includeEmpty: true,
      },
    ],
  },
];

export const buildWorkspacePreviewYaml = ({
  workspaceType,
  workspaceLabel,
  groupName,
  imageRef,
  gpuConfig,
  cpuConfig,
  selectedDatasets,
}) => {
  const isGpuWorkspace = workspaceType === "gpu";
  const computeConfig = isGpuWorkspace ? gpuConfig : cpuConfig;

  return renderTemplate(workspacePreviewTemplate, {
    experimentType: "workspace",
    workspaceType,
    workspaceLabel: workspaceLabel.trim(),
    groupName,
    image:
      imageRef.trim() ||
      `base-images/${isGpuWorkspace ? "gpu" : "cpu"}-base:latest`,
    nodeCount: parseOptionalInt(computeConfig?.nodeCount),
    gpuProduct: isGpuWorkspace ? gpuConfig.gpuProduct : null,
    gpuCount: isGpuWorkspace ? parseOptionalInt(gpuConfig.gpuCount) : null,
    cpuRequestCores: parseOptionalInt(computeConfig?.cpuCores),
    memoryRequestGb: parseOptionalInt(computeConfig?.memory),
    burstStorageEnabled: isGpuWorkspace ? gpuConfig.burstBufferEnabled : false,
    datasets: normalizeDatasets(selectedDatasets),
  });
};

export const buildJobPreviewYaml = ({
  jobType,
  jobLabel,
  groupName,
  imageRef,
  walltimeHours,
  gpuConfig,
  cpuConfig,
  selectedDatasets,
  commands,
}) => {
  const isGpuJob = jobType === "gpu";
  const computeConfig = isGpuJob ? gpuConfig : cpuConfig;
  const parsedWalltime = parseInt(walltimeHours, 10);

  return renderTemplate(jobPreviewTemplate, {
    experimentType: "job",
    jobType,
    jobLabel: jobLabel.trim(),
    groupName,
    image:
      imageRef.trim() || `base-images/${isGpuJob ? "gpu" : "cpu"}-base:latest`,
    walltimeHours: Number.isInteger(parsedWalltime) ? parsedWalltime : null,
    nodeCount: parseOptionalInt(computeConfig?.nodeCount),
    gpuProduct: isGpuJob ? gpuConfig.gpuProduct : null,
    gpuCount: isGpuJob ? parseOptionalInt(gpuConfig.gpuCount) : null,
    cpuRequestCores: parseOptionalInt(computeConfig?.cpuCores),
    memoryRequestGb: parseOptionalInt(computeConfig?.memory),
    datasets: normalizeDatasets(selectedDatasets),
    commands: commands || [],
  });
};
